import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Wine, Clock, MapPin, Phone, Mail, Star, Quote, ChefHat, Utensils } from 'lucide-react';
import { restaurantInfo, menuData, testimonials, galleryImages, heroImages } from '../mock';

const RestaurantPage = () => {
  const [isVisible, setIsVisible] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: ''
  });

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: true
          }));
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(`Reservation request submitted for ${formData.name} on ${formData.date} at ${formData.time} for ${formData.guests} guests. We'll contact you shortly!`);
    setFormData({
      name: '',
      email: '',
      phone: '',
      date: '',
      time: '',
      guests: ''
    });
  };

  return (
    <div className="restaurant-page">
      {/* Header */}
      <header style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        backgroundColor: 'rgba(75, 28, 47, 0.95)',
        backdropFilter: 'blur(10px)',
        zIndex: 1000,
        padding: '1rem 0'
      }}>
        <div className="container">
          <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ 
              fontSize: '1.8rem', 
              fontFamily: 'var(--font-serif)', 
              fontWeight: '700',
              color: 'var(--color-antique-gold)'
            }}>
              {restaurantInfo.name}
            </div>
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
              <a href="#menu" style={{ color: 'var(--color-cream)', textDecoration: 'none', fontWeight: '500' }}>Menu</a>
              <a href="#gallery" style={{ color: 'var(--color-cream)', textDecoration: 'none', fontWeight: '500' }}>Gallery</a>
              <a href="#reservations" style={{ color: 'var(--color-cream)', textDecoration: 'none', fontWeight: '500' }}>Reservations</a>
              <Button className="btn-primary">Reserve Now</Button>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        id="hero"
        data-animate
        style={{
          minHeight: '100vh',
          backgroundImage: `url(${heroImages.main})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          position: 'relative'
        }}
      >
        <div className="hero-overlay" style={{ position: 'absolute', inset: 0 }}></div>
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className={`${isVisible.hero ? 'fade-in-up' : ''}`} style={{ color: 'var(--color-cream)' }}>
            <h1 style={{ 
              fontSize: 'clamp(3rem, 6vw, 5rem)', 
              marginBottom: '1rem',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
            }}>
              {restaurantInfo.tagline}
            </h1>
            <p style={{ 
              fontSize: '1.3rem', 
              marginBottom: '2.5rem', 
              maxWidth: '600px', 
              margin: '0 auto 2.5rem',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}>
              {restaurantInfo.description}
            </p>
            <Button 
              className="btn-primary" 
              style={{ fontSize: '1.2rem', padding: '16px 40px' }}
              onClick={() => document.getElementById('reservations').scrollIntoView({ behavior: 'smooth' })}
            >
              Reserve a Table
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" data-animate className="section section-cream">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
            <div className={`${isVisible.about ? 'fade-in-up' : ''}`}>
              <img 
                src={heroImages.about}
                alt="Chef presenting gourmet dish"
                style={{ width: '100%', borderRadius: 'var(--border-radius-lg)', boxShadow: 'var(--shadow-elegant)' }}
              />
            </div>
            <div className={`${isVisible.about ? 'fade-in-up-delay' : ''}`}>
              <h2 className="text-bordeaux" style={{ marginBottom: '1.5rem' }}>
                Crafted with Passion & Elegance
              </h2>
              <p style={{ fontSize: '1.2rem', marginBottom: '2rem' }}>
                At Maison Élégance, we blend tradition and innovation, creating dining experiences that linger long after the last sip of wine.
              </p>
              <p style={{ marginBottom: '2rem' }}>
                Our master chefs source the finest ingredients from local purveyors and international suppliers, ensuring each dish tells a story of culinary excellence and artistic presentation.
              </p>
              <div style={{ display: 'flex', gap: '2rem', marginTop: '2rem' }}>
                <div style={{ textAlign: 'center' }}>
                  <ChefHat className="text-gold" size={32} style={{ margin: '0 auto 0.5rem' }} />
                  <p style={{ fontWeight: '600', color: 'var(--color-bordeaux)' }}>Master Chefs</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Wine className="text-gold" size={32} style={{ margin: '0 auto 0.5rem' }} />
                  <p style={{ fontWeight: '600', color: 'var(--color-bordeaux)' }}>Curated Wines</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Utensils className="text-gold" size={32} style={{ margin: '0 auto 0.5rem' }} />
                  <p style={{ fontWeight: '600', color: 'var(--color-bordeaux)' }}>Fine Dining</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" data-animate className="section section-bordeaux">
        <div className="container">
          <div className={`${isVisible.menu ? 'fade-in-up' : ''}`} style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="text-cream">Our Culinary Creations</h2>
            <p className="text-cream" style={{ fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto' }}>
              Each dish is carefully crafted with premium ingredients and presented with artistic flair
            </p>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '3rem' }}>
            {Object.entries(menuData).map(([category, items]) => (
              <Card key={category} className="card-elegant">
                <h3 className="text-bordeaux" style={{ 
                  textTransform: 'capitalize', 
                  marginBottom: '2rem',
                  textAlign: 'center',
                  borderBottom: '2px solid var(--color-antique-gold)',
                  paddingBottom: '1rem'
                }}>
                  {category === 'wines' ? 'Wine Selection' : category}
                </h3>
                {items.map((item, index) => (
                  <div key={index} className="menu-item">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                      <div style={{ flex: 1 }}>
                        <h4 className="text-bordeaux" style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                          {item.name}
                        </h4>
                        <p style={{ fontSize: '0.95rem', color: 'var(--color-charcoal)', lineHeight: '1.5' }}>
                          {item.description}
                        </p>
                      </div>
                      <div className="menu-price" style={{ fontFamily: 'var(--font-serif)' }}>
                        {item.price}
                      </div>
                    </div>
                  </div>
                ))}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" data-animate className="section section-cream">
        <div className="container-wide">
          <div className={`${isVisible.gallery ? 'fade-in-up' : ''}`} style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="text-bordeaux">Culinary Gallery</h2>
            <p style={{ fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto' }}>
              Discover the artistry and elegance that defines every moment at Maison Élégance
            </p>
          </div>
          
          <div className="gallery-grid">
            {galleryImages.map((image) => (
              <div key={image.id} className="gallery-item">
                <img 
                  src={image.src}
                  alt={image.alt}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div className="gallery-overlay">
                  {image.title}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reservations Section */}
      <section id="reservations" data-animate className="section section-cream">
        <div className="container">
          <div className={`${isVisible.reservations ? 'fade-in-up' : ''}`} style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="text-bordeaux">Reserve Your Table</h2>
            <p style={{ fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto' }}>
              Secure your dining experience at Maison Élégance. We recommend booking in advance.
            </p>
          </div>

          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <form onSubmit={handleSubmit} className="elegant-form">
              <div className="form-row">
                <div className="form-field">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </div>
                <div className="form-field">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                  />
                </div>
                <div className="form-field">
                  <Label htmlFor="guests">Number of Guests</Label>
                  <Select value={formData.guests} onValueChange={(value) => handleInputChange('guests', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select guests" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8].map(num => (
                        <SelectItem key={num} value={num.toString()}>{num} {num === 1 ? 'Guest' : 'Guests'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <Label htmlFor="date">Preferred Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="form-field">
                  <Label htmlFor="time">Preferred Time</Label>
                  <Select value={formData.time} onValueChange={(value) => handleInputChange('time', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {['5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM'].map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button type="submit" className="btn-primary" style={{ width: '100%', fontSize: '1.1rem', padding: '16px' }}>
                Book Your Experience
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" data-animate className="section section-bordeaux">
        <div className="container">
          <div className={`${isVisible.testimonials ? 'fade-in-up' : ''}`} style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="text-cream">What Our Guests Say</h2>
            <p className="text-cream" style={{ fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto' }}>
              Discover why discerning diners choose Maison Élégance for their most memorable occasions
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card">
                <Quote className="quote-mark" style={{ marginBottom: '1rem' }} />
                <p className="text-cream" style={{ fontSize: '1.1rem', marginBottom: '1.5rem', fontStyle: 'italic' }}>
                  "{testimonial.text}"
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ display: 'flex', color: 'var(--color-antique-gold)' }}>
                    {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                  </div>
                  <div>
                    <p className="text-gold" style={{ fontWeight: '600', margin: 0 }}>
                      {testimonial.author}
                    </p>
                    <p className="text-cream" style={{ fontSize: '0.9rem', margin: 0, opacity: 0.8 }}>
                      {testimonial.location}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section 
        id="cta"
        data-animate
        style={{
          minHeight: '60vh',
          backgroundImage: `url(${heroImages.cta})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          position: 'relative'
        }}
      >
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(75, 28, 47, 0.8) 0%, rgba(201, 166, 70, 0.6) 100%)'
        }}></div>
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className={`${isVisible.cta ? 'fade-in-up' : ''}`}>
            <h2 style={{ 
              fontSize: 'clamp(2.5rem, 5vw, 4rem)', 
              color: 'var(--color-antique-gold)',
              marginBottom: '1rem',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
            }}>
              Your Table Awaits
            </h2>
            <p style={{ 
              fontSize: '1.3rem', 
              color: 'var(--color-cream)', 
              marginBottom: '2rem',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}>
              Join us for an unforgettable culinary journey
            </p>
            <Button 
              className="btn-primary" 
              style={{ fontSize: '1.2rem', padding: '16px 40px' }}
              onClick={() => document.getElementById('reservations').scrollIntoView({ behavior: 'smooth' })}
            >
              Reserve Now
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: 'var(--color-bordeaux)', padding: '3rem 0 1rem', color: 'var(--color-cream)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginBottom: '2rem' }}>
            <div>
              <h3 style={{ color: 'var(--color-antique-gold)', marginBottom: '1rem', fontFamily: 'var(--font-serif)' }}>
                {restaurantInfo.name}
              </h3>
              <p style={{ marginBottom: '1rem' }}>
                Experience fine dining where every detail is a celebration of flavor and elegance.
              </p>
            </div>
            
            <div>
              <h4 style={{ color: 'var(--color-antique-gold)', marginBottom: '1rem' }}>Contact</h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Phone size={16} />
                <span>{restaurantInfo.phone}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Mail size={16} />
                <span>{restaurantInfo.email}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <MapPin size={16} style={{ marginTop: '2px' }} />
                <span>{restaurantInfo.address}</span>
              </div>
            </div>

            <div>
              <h4 style={{ color: 'var(--color-antique-gold)', marginBottom: '1rem' }}>Hours</h4>
              {Object.entries(restaurantInfo.hours).map(([day, hours]) => (
                <div key={day} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ textTransform: 'capitalize', fontWeight: '500' }}>{day}:</span>
                  <span>{hours}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ 
            textAlign: 'center', 
            paddingTop: '2rem', 
            borderTop: '1px solid rgba(201, 166, 70, 0.3)' 
          }}>
            <p style={{ margin: 0, opacity: 0.8 }}>
              © 2025 Maison Élégance. All rights reserved. | Crafted with passion for culinary excellence.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RestaurantPage;